import useTranslation from 'next-translate/useTranslation';
import type { BadgeVariant, MantineSize, MantineColor } from '@mantine/core';
import { Badge, useMantineTheme, createStyles } from '@mantine/core';
import { ContestType } from '@betterpool/api-types/contests-service';
import React from 'react';

import { Text } from '~/domains/design-system/Text';

type ContestTypeBadgeProps = {
  type: ContestType | 'QUICK_PICKS' | 'unknown';
  colorScheme?: 'light' | 'dark';
  variant?: 'default' | 'text' | 'unstyled';
  color?: string;
  altLabel?: string;
};

const useStyles = createStyles({
  label: {
    fontSize: 10,
    textTransform: 'capitalize',
  },
});

type ContestTypeComponentProps = {
  variant: 'default' | 'text' | 'unstyled';
  color: string;
  styleProps: {
    variant: BadgeVariant,
    c: MantineColor,
    bg: MantineColor,
    size: MantineSize,
    radius: MantineSize,
    px: number,
    className: string,
  };
  children: React.ReactNode;
};
function ContestTypeComponent({
  variant,
  color,
  styleProps,
  children,
}: ContestTypeComponentProps) {
  if (variant === 'text') return <Text color={color}>{children}</Text>;
  if (variant === 'unstyled') return children;

  return <Badge {...styleProps}>{children}</Badge>;
}

function ContestTypeBadge({
  type,
  altLabel,
  colorScheme = 'light',
  color,
  variant = 'default',
}: ContestTypeBadgeProps) {
  const { t } = useTranslation('common');
  const theme = useMantineTheme();
  const { classes } = useStyles();

  const styleProps = {
    variant: 'filled' as BadgeVariant,
    c: (colorScheme === 'light' ? theme.white : theme.colors.gray[9]) as MantineColor,
    bg: (colorScheme === 'light' ? theme.colors.gray[9] : theme.colors.gray[3]) as MantineColor,
    size: 'sm ' as MantineSize,
    radius: 'lg' as MantineSize,
    px: 6,
    className: classes.label,
  };
  const isTextVariant = variant === 'text';

  switch (type) {
    case ContestType.TEAM_PICK_EM:
      return (
        <ContestTypeComponent
          variant={variant}
          color={color}
          styleProps={{ bg: true, ...styleProps }}
        >
          {t('contest.pickEm')}
        </ContestTypeComponent>
      );
    case ContestType.TEAM_SURVIVOR:
      return <ContestTypeComponent variant={variant} color={color} styleProps={styleProps}>{t('contest.survivor')}</ContestTypeComponent>;
    case ContestType.PLAYER_TIER:
      return <ContestTypeComponent variant={variant} color={color} styleProps={styleProps}>{t('contest.tiers')}</ContestTypeComponent>;
    case ContestType.PLAYER_ONE_AND_DONE:
      return <ContestTypeComponent variant={variant} color={color} styleProps={styleProps}>{t('contest.oneAndDone')}</ContestTypeComponent>;
    case ContestType.PLAYER_SURVIVOR:
      return <ContestTypeComponent variant={variant} color={color} styleProps={styleProps}>{t('contest.playerSurvivor')}</ContestTypeComponent>;
    case ContestType.SQUARES:
      return <ContestTypeComponent variant={variant} color={color} styleProps={styleProps}>{altLabel || t('contest.squares')}</ContestTypeComponent>;
    case ContestType.TARGET_SCORE_BINGO:
      return <ContestTypeComponent variant={variant} color={color} styleProps={styleProps}>{altLabel || t('contest.targetScore')}</ContestTypeComponent>;
    case ContestType.WINS_POOL:
      return <ContestTypeComponent variant={variant} color={color} styleProps={styleProps}>{altLabel || t('contest.wins')}</ContestTypeComponent>;
    case ContestType.SHARES:
      return <ContestTypeComponent variant={variant} color={color} styleProps={styleProps}>{altLabel || t('contest.shares')}</ContestTypeComponent>;
    case ContestType.TEAM_BRACKETS:
      return <ContestTypeComponent variant={variant} color={color} styleProps={styleProps}>{altLabel || t('contest.brackets')}</ContestTypeComponent>;
    case 'QUICK_PICKS':
      return <ContestTypeComponent variant={variant} color={color} styleProps={styleProps}>{t('contest.quickPicks')}</ContestTypeComponent>;
    default:
      return altLabel ?
        <ContestTypeComponent variant={variant} color={color} styleProps={styleProps}>{altLabel}</ContestTypeComponent>
        : null;
  }
}

export default ContestTypeBadge;
